// src/Context/Cartcontext.js
import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { API_BASE } from "../Services/Api";
import { useAuth } from "./AuthContext";

const CartContext = createContext();

/*
 * The cart API is authenticated. The login cookie is a third-party cookie
 * whenever the React site and the API sit on different hosts, and Chrome
 * blocks those (always in Incognito), which made every cart call return 401.
 *
 * Api.js already solves this by sending the saved token as a Bearer header,
 * so the cart calls now do the same.
 */
const cartHeaders = () => {
  const token = sessionStorage.getItem("access_token");

  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const CartProvider = ({ children }) => {
  const { user, authLoading } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch cart only after auth finished and user exists
  useEffect(() => {
    // wait for auth initialization
    if (authLoading) return;

    if (!user) {
      // not logged in — clear cart and stop
      setCartItems([]);
      setLoading(false);
      return;
    }

  
    

    let cancelled = false;
    const fetchCart = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/cart`, {
          method: "GET",
          credentials: "include",
          headers: cartHeaders(),
        });
        if (!res.ok) throw new Error("Failed to fetch cart");
        const data = await res.json();
        if (!cancelled) setCartItems(data.cart || []);
      } catch (err) {
        console.error("Error fetching cart:", err);
        if (!cancelled) setCartItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCart();
    return () => { cancelled = true; };
  }, [user, authLoading]);
  
// -----------------------------------------------------------------------------------
  // add to cart
  const addToCart = async (courseId) => {
    try {
      const res = await fetch(`${API_BASE}/cart/add`, {
        method: "POST",
        credentials: "include",
        headers: cartHeaders(),
        body: JSON.stringify({ course_id: courseId }),
      });

      if (!res.ok) throw new Error("Failed to add to cart");
      // refresh cart after add
      const cartRes = await fetch(`${API_BASE}/cart`, {
        method: "GET",
        credentials: "include",
        headers: cartHeaders(),
      });
      if (!cartRes.ok) throw new Error("Failed to refresh cart");
      const data = await cartRes.json();
      setCartItems(data.cart || []);
    } catch (err) {
      console.error("Error adding to cart:", err);
      throw err;
    }
  };

// -----------------------------------------------------------------------------------

  // remove from cart
  const removeFromCart = async (courseId) => {
    try {
      const res = await fetch(`${API_BASE}/cart/remove`, {
        method: "POST",
        credentials: "include",
        headers: cartHeaders(),
        body: JSON.stringify({ course_id: courseId }),
      });
      if (!res.ok) throw new Error("Failed to remove from cart");
      // optimistic update
      setCartItems((prev) => prev.filter((c) => c.id !== courseId));
    } catch (err) {
      console.error("Error removing from cart:", err);
      throw err;
    }
  };

// -----------------------------------------------------------------------------------

  const clearCart = async () => {
    try {
      const res = await fetch(`${API_BASE}/cart/clear`, {
        method: "POST",
        credentials: "include",
        headers: cartHeaders(),
      });
      if (!res.ok) throw new Error("Failed to clear cart");
      setCartItems([]);
    } catch (err) {
      console.error("Error clearing cart:", err);
      throw err;
    }
  };

  // -----------------------------------------------------------------------------------

  const totalPrice = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + (item.final_price || 0), 0);
  }, [cartItems]);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, loading, clearCart, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
